// components/MobilePoiDrawer.jsx
import React, { useState, useEffect } from 'react';
import { Compass, Route, ArrowLeft, Ticket, ChevronDown, ChevronUp, User2, Globe, Facebook, Instagram, MapPin, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getLocalizedPoiName,
  getLocalizedPoiDescription,
  getLocalizedPoiType
} from '../utils/localizationHelpers';

import { extractInstagramHandle } from '../utils/socialHelpers';

import config from '../config';

import { jwtDecode } from 'jwt-decode';

const MobilePoiDrawer = ({
  poi,
  onClose,
  onNavigateFrom,
  onNavigateTo,
  poiNavigationMode,
  setActiveSidebarTab,
  setMapListView,
  setIsSidebarOpen
}) => {
  
  const { t } = useTranslation();

  const [offers, setOffers] = useState([]);

  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
  const [expandedOfferId, setExpandedOfferId] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
        try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
        } catch (e) {
        console.error('Invalid token');
        }
    }
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
        if (!poi?._id) return;
        try {
        const url = config.getApiUrl(`offers?poiId=${poi._id}`);
        const res = await fetch(url);
        const data = await res.json();
        if (data.success) {
            setOffers(data.offers);
        } else {
            console.error('Backend returned success: false');
        }
        } catch (err) {
        console.error('Error fetching offers for POI:', err);
        }
    };

    fetchOffers();
  }, [poi]);

  if (!poi) return null;

    return (
    <div
        className="fixed inset-0 z-40 bg-black bg-opacity-30 flex items-center justify-center"
        onClick={onClose}
    >
        <div
        className="relative bg-white rounded-xl w-full max-w-md mx-4 p-4 max-h-[80vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
        >
        <button
            onClick={onClose}
            className="absolute top-2 right-3 text-gray-400 hover:text-gray-600"
            aria-label="Close"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        </button>

        <h3 className="font-bold text-xl text-[#1f2937] mb-2">
            {getLocalizedPoiName(poi)}
        </h3>

        {poi.photo ? (
            <img
            src={poi.photo}
            alt={getLocalizedPoiName(poi)}
            className="w-full h-48 object-cover rounded mb-4"
            />
        ) : (
            <div className="bg-gray-200 h-48 rounded mb-4 flex items-center justify-center">
            <span className="text-gray-500">{t('map.noImageAvailable', 'No image available')}</span>
            </div>
        )}

        {getLocalizedPoiDescription(poi) && (
            <div className="mb-4">
            <p className="text-sm text-[#6b7280]">
                {getLocalizedPoiDescription(poi)}
            </p>
            </div>
        )}

        {offers.length > 0 && (
            <div className="mt-6">
                <h4 className="text-md font-semibold mb-2 text-gray-800">
                {t('map.availableOffers', 'Available offers')}
                </h4>
                <div className="space-y-3">
                {offers.map((offer) => {
                    const name = offer.name?.[preferredLanguage] || offer.name?.en || 'Unnamed';
                    const description = offer.description?.[preferredLanguage] || offer.description?.en || '';
                    const isExpanded = expandedOfferId === offer._id;

                    return (
                        <div
                        key={offer._id}
                        className="border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition relative"
                        >
                        <div
                            className="flex items-center justify-between mb-2 cursor-pointer"
                            onClick={() => setExpandedOfferId(isExpanded ? null : offer._id)}
                        >
                            <div className="flex items-center space-x-2">
                            {offer.image ? (
                                <img
                                src={offer.image}
                                alt={name}
                                className="h-10 w-10 rounded-full object-cover border border-gray-300"
                                onError={(e) => (e.target.style.display = 'none')}
                                />
                            ) : (
                                <Ticket className="h-5 w-5 text-green-500" />
                            )}

                            <h3 className="text-sm font-semibold text-gray-800">{name}</h3>
                            </div>
                            {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                        </div>

                        <p className="text-xs text-gray-600 mb-2">{description}</p>

                        {offer.partnerId && (
                            <div className="flex items-center text-xs text-gray-500 mb-2">
                            <User2 className="w-4 h-4 mr-1" />
                            <span>
                                {offer.partnerId.businessName || offer.partnerId.firstName || 'Business'}
                            </span>
                            </div>
                        )}

                        {offer.price && (
                            <p className="text-sm font-medium text-gray-900">
                            💶 {offer.price.toFixed(2)} €
                            </p>
                        )}

                        {isExpanded && (
                        <div className="mt-3 space-y-3">
                            {offer.image && (
                            <img
                                src={offer.image}
                                alt={name}
                                className="w-full h-40 object-cover rounded-lg shadow-sm"
                                onError={(e) => (e.target.style.display = 'none')}
                            />
                            )}

                            {userId ? (
                            <div className="text-center">
                                <p className="text-xs text-gray-500 mb-1">
                                {t('map.showThisCode', 'Show this QR code to redeem the offer')}
                                </p>
                                <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${window.location.origin}/offers/redeem/${offer.qrCode}/${userId}`}
                                alt="QR Code"
                                className="mx-auto rounded shadow"
                                />
                            </div>
                            ) : (
                            <div className="p-6 text-center text-gray-600">
                                {t('offer.loginToClaim', 'Please log in to claim offer.')}

                                <div>
                                    <button
                                    onClick={() => {
                                        setActiveSidebarTab('profile');
                                        setMapListView('list');
                                        setIsSidebarOpen(true);
                                        onClose(); // Close drawer
                                    }}
                                    className="mt-2 text-blue-600 underline text-sm"
                                    >
                                    {t('profile.goToProfile', 'Go to Profile')}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                        )}

                        </div>
                    );
                    })}
                </div>
            </div>
        )}

        <div className="mb-4 space-y-3">
            {poi.openingTimes && (
                <div className="flex items-center text-sm text-[#4b5563]">
                <Clock className="h-4 w-4 mr-2 text-[#6b7280]" />
                <span>{poi.openingTimes}</span>
                </div>
            )}

            {poi.website && (
                <div className="flex items-center text-sm text-[#4b5563]">
                <Globe className="h-4 w-4 mr-2 text-[#6b7280]" />
                <a
                    href={poi.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                >
                    {poi.website}
                </a>
                </div>
            )}
            {poi.instagram && (
            <div className="flex items-center text-sm text-[#4b5563]">
                <Instagram className="h-4 w-4 mr-2 text-[#6b7280]" />
                <a
                href={poi.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 hover:underline"
                >
                @{extractInstagramHandle(poi.instagram)}
                </a>
            </div>
            )}
            {poi.facebook && (
                <div className="flex items-center text-sm text-[#4b5563]">
                <Facebook className="h-4 w-4 mr-2 text-[#6b7280]" />
                <a
                    href={poi.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline truncate"
                >
                    Facebook
                </a>
                </div>
            )}
            {poi.address && (
                <div className="flex items-center text-sm text-[#4b5563]">
                <MapPin className="h-4 w-4 mr-2 text-[#6b7280]" />
                <span>{poi.address}</span>
                </div>
            )}
        </div>

        <div className="flex flex-col space-y-2 mt-6">
            <button
            className="w-full bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
            onClick={() => {
                onClose(); // close drawer
                onNavigateTo(poi); // handle "go here"
            }}            >
            <Compass className="h-4 w-4 mr-2" />
            {t('map.planner.navigateToThisPoi', 'Navigate there')}
            </button>

            <button
            className="w-full bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-600 transition-colors flex items-center justify-center"
            onClick={() => {
                onClose(); // close drawer
                onNavigateFrom(poi); // handle "start from here"
            }}            >
            <Route className="h-4 w-4 mr-2" />
            {t('map.planner.navigateFromThisPoi', 'Navigate from This POI')}
            </button>
        </div>
        </div>
    </div>
    );
};

export default MobilePoiDrawer;
